/* ============================================================
   Landed Cost & Demurrage Guard

   Two jobs in one screen, because they are the same decision:
     1. What does this consignment actually cost me on the ground?
     2. What is the clock costing me while I work that out?

   Demurrage and detention run at USD 150–300 per container per day and
   are almost always discovered at invoice. The countdown here exists to
   move that discovery forward by a week.
   ============================================================ */

import {
  DUTY_BANDS,
  dutyBand,
  assessDuty,
  assessDemurrage,
  marginalDayCost,
  DEFAULT_TIERS,
  VAT_RATE,
  IDF_RATE,
  RDL_RATE
} from '../../data/duty.js';
import { html, raw } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { money, number, percent, parseNumber } from '../../shared/format.js';
import { saveRecord } from '../../shared/records.js';
import { prefillForm, rememberProfile } from '../../shared/profile.js';
import { PrefillNotice } from '../../components/PrefillNotice.js';
import { PrintButton } from '../../components/PrintButton.js';
import { readable } from '../../shared/tone.js';

const CHARGE_FIELDS = [
  { id: 'thc', label: 'Terminal handling / port', color: 'var(--ca-chart-1)' },
  { id: 'agent', label: 'Clearing agent fee', color: 'var(--ca-chart-2)' },
  { id: 'transport', label: 'Inland transport', color: 'var(--ca-chart-3)' },
  { id: 'inspection', label: 'PVoC / inspection', color: 'var(--ca-chart-4)' },
  { id: 'insurance', label: 'Local insurance', color: 'var(--ca-chart-5)' },
  { id: 'other', label: 'Other charges', color: 'var(--ca-chart-7)' }
];

export function LandedCostPage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>Landed Cost &amp; Demurrage Guard</h1>
      <p>
        What the consignment costs on the ground, and what every extra day at the terminal is
        adding to it. Nothing is sent anywhere — the numbers stay in this browser.
      </p>
    </header>

    <div id="lc-form">
      <section class="section">
        <h2 class="section-title">Consignment</h2>
        <div class="card">
          <div class="grid-2">
            <label class="field">
              <span class="field-label">Customs value, CIF (USD)</span>
              <input id="cif" class="input-field num" type="number" inputmode="decimal"
                     min="0" step="1" placeholder="45000" />
            </label>
            <label class="field">
              <span class="field-label">Containers</span>
              <input id="containers" class="input-field num" type="number" inputmode="numeric"
                     min="1" step="1" value="1" />
            </label>
          </div>

          <label class="field">
            <span class="field-label">Import duty band (EAC CET)</span>
            <select id="duty-band" class="input-field">
              ${DUTY_BANDS.map((b) => html`<option value="${b.id}">${b.label}</option>`)}
            </select>
            <span class="field-hint" id="band-hint"></span>
          </label>

          <label class="field" style="margin-bottom: 0;">
            <span class="field-label">Excise duty rate (%), if any</span>
            <input id="excise" class="input-field num" type="number" inputmode="decimal"
                   min="0" max="100" step="0.5" placeholder="0" />
            <span class="field-hint">
              Leave blank unless the goods are excisable — spirits, tobacco, sugar confectionery,
              certain plastics and cosmetics.
            </span>
          </label>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">Local charges (USD)</h2>
        <div class="card">
          <div class="grid-2">
            ${CHARGE_FIELDS.map(
              (f) => html`
                <label class="field">
                  <span class="field-label" style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
                    ${f.label}
                  </span>
                  <input id="chg-${f.id}" class="input-field num" type="number"
                         inputmode="decimal" min="0" step="1" placeholder="0" />
                </label>
              `
            )}
          </div>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">Free time &amp; the clock</h2>
        <div class="card">
          <div class="grid-2">
            <label class="field">
              <span class="field-label">Free days allowed</span>
              <input id="free-days" class="input-field num" type="number" inputmode="numeric"
                     min="0" step="1" value="4" />
            </label>
            <label class="field">
              <span class="field-label">Days held so far</span>
              <input id="days-held" class="input-field num" type="number" inputmode="numeric"
                     min="0" step="1" value="0" />
            </label>
          </div>
          <div class="grid-2">
            <label class="field">
              <span class="field-label">Rate, days 1–3</span>
              <input id="tier1" class="input-field num" type="number" inputmode="decimal"
                     min="0" step="1" value="45" />
            </label>
            <label class="field">
              <span class="field-label">Rate, days 4–7</span>
              <input id="tier2" class="input-field num" type="number" inputmode="decimal"
                     min="0" step="1" value="90" />
            </label>
          </div>
          <label class="field" style="margin-bottom: 0;">
            <span class="field-label">Rate, day 8 onward</span>
            <input id="tier3" class="input-field num" type="number" inputmode="decimal"
                   min="0" step="1" value="150" />
            <span class="field-hint">
              Per container per day. Defaults reflect published 2026 benchmark ranges of
              USD 150–300 at the top tier; replace them with your carrier's schedule.
            </span>
          </label>
        </div>
      </section>

      <section class="section">
        <div class="card card-tinted">
          <label class="row" style="gap: var(--ca-space-3); cursor: pointer; align-items: flex-start;">
            <input type="checkbox" id="levy-vat" style="margin-top: 3px; width: 17px; height: 17px; accent-color: var(--brand-accent);" />
            <span style="flex: 1; font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
              <strong style="font-weight: var(--ca-weight-medium); color: var(--ca-text-secondary);">
                Include IDF and RDL in the VAT base
              </strong><br />
              By default they are treated as levies outside the VAT base, following KRA's order
              of assessment. Entries vary by commodity and by how the agent lodges them — tick
              this to match yours.
            </span>
          </label>
        </div>
      </section>

      <div id="lc-error"></div>

      <button class="btn btn-primary btn-block" id="lc-calculate" type="button"
              style="padding: var(--ca-space-4);">
        Calculate landed cost
      </button>
    </div>

    <div id="landed-cost-results" hidden></div>
  `;

  const bandSelect = page.querySelector('#duty-band');
  const bandHint = page.querySelector('#band-hint');
  const paintHint = () => {
    bandHint.textContent = dutyBand(bandSelect.value).examples;
  };
  bandSelect.addEventListener('change', paintHint);
  paintHint();

  const applied = prefillForm(page, { containers: 'containers', freeDays: 'free-days' });
  if (applied.length > 0) {
    const notice = PrefillNotice(applied, page);
    if (notice) page.querySelector('#lc-form').prepend(notice);
  }

  page.querySelector('#lc-calculate').addEventListener('click', () => calculate(page));

  return page;
}

function calculate(page) {
  const val = (id) => parseNumber(page.querySelector(`#${id}`).value);

  const cif = val('cif');
  const containers = val('containers') ?? 1;
  const band = dutyBand(page.querySelector('#duty-band').value);
  const exciseRate = (val('excise') ?? 0) / 100;
  const freeDays = val('free-days') ?? 0;
  const daysHeld = val('days-held') ?? 0;
  const levyOnVatBase = page.querySelector('#levy-vat').checked;

  const tiers = [
    { fromDay: 1, toDay: 3, rate: val('tier1') ?? 0 },
    { fromDay: 4, toDay: 7, rate: val('tier2') ?? 0 },
    { fromDay: 8, toDay: null, rate: val('tier3') ?? 0 }
  ];

  const charges = {};
  for (const field of CHARGE_FIELDS) charges[field.id] = val(`chg-${field.id}`) ?? 0;

  const errors = [];
  if (cif === null || cif <= 0) errors.push('Enter a customs value greater than zero.');
  if (containers < 1) errors.push('There must be at least one container.');
  if (freeDays < 0) errors.push('Free days cannot be negative.');
  if (daysHeld < 0) errors.push('Days held cannot be negative.');
  if (exciseRate < 0 || exciseRate > 1) errors.push('Excise rate must be between 0 and 100%.');

  /* A ceiling as well as a floor, and a warning rather than a refusal.

     The checks above are all lower bounds — nothing negative, nothing
     zero where a divisor needs a value. There was no upper bound at all,
     so a CIF of 1e15 produced a landed cost of US$1,205,000,000,000,525
     stated with exactly the confidence of a right answer.

     The quadrillion is not the danger; nobody misses that. The danger is
     the ordinary units slip — KES typed into a USD field is about 130x,
     cents where units were wanted is 100x — which on a normal
     consignment lands somewhere between two and twenty million per
     container and looks like a number rather than a mistake.

     Two million per container is the threshold. A forty-foot container
     of the most valuable ordinary cargo — pharmaceuticals, high-end
     electronics — runs to one or two million, so a genuine consignment
     at that level gets a question it can dismiss, while a 100x slip on
     anything typical is caught. It warns and still computes, because the
     tool does not know which of the two it is looking at and the
     operator does. */
  const CIF_PER_CONTAINER_CAUTION = 2_000_000;
  const cautions = [];
  if (cif !== null && containers >= 1 && cif / containers > CIF_PER_CONTAINER_CAUTION) {
    cautions.push(
      `That is ${money(cif / containers)} of cargo per container. Worth a second look at the ` +
      `units — a customs value entered in KES, or in cents, reads about a hundred times high. ` +
      `The figures below are calculated as entered.`
    );
  }

  const errorSlot = page.querySelector('#lc-error');
  if (errors.length > 0) {
    errorSlot.innerHTML = html`
      <div class="notice notice-danger" style="margin-bottom: var(--ca-space-4);">
        <span style="flex-shrink:0;margin-top:2px;">${raw(icon('alert', 15))}</span>
        <div>
          <strong style="font-weight: var(--ca-weight-medium);">Cannot calculate yet</strong>
          <ul style="margin: var(--ca-space-1) 0 0 var(--ca-space-4); font-size: var(--ca-text-xs);">
            ${errors.map((e) => html`<li>${e}</li>`)}
          </ul>
        </div>
      </div>
    `;
    errorSlot.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return;
  }
  errorSlot.innerHTML = '';

  const duty = assessDuty({ cif, dutyRate: band.rate, exciseRate, levyOnVatBase });
  const demurrage = assessDemurrage({ daysHeld, freeDays, tiers, containers });
  const marginal = marginalDayCost({ daysHeld, freeDays, tiers, containers });

  const chargesTotal = Object.values(charges).reduce((a, b) => a + b, 0);
  const landedTotal = duty.landedBeforeCharges + chargesTotal + demurrage.total;

  renderResults(page, {
    cif,
    containers,
    band,
    exciseRate,
    levyOnVatBase,
    duty,
    charges,
    chargesTotal,
    demurrage,
    marginal,
    freeDays,
    daysHeld,
    landedTotal,
    cautions
  });

  void saveRecord('landed-cost', {
    cif,
    containers,
    dutyBand: band.id,
    landedTotal,
    demurrage: demurrage.total
  });
}

function renderResults(page, r) {
  page.querySelector('#lc-form').hidden = true;

  const results = page.querySelector('#landed-cost-results');
  results.hidden = false;
  results.className = 'animate-fade-in';
  results.replaceChildren();

  /* At the top of the results rather than by the form. The form is
     hidden the moment this renders and the page moves to the answer, so
     a caution left behind next to the inputs is a caution nobody reads. */
  if (r.cautions?.length) {
    const caution = document.createElement('div');
    caution.className = 'notice notice-warning';
    caution.setAttribute('role', 'status');
    caution.style.marginBottom = 'var(--ca-space-5)';
    caution.innerHTML = html`
      <span style="flex-shrink:0;margin-top:2px;">${raw(icon('alert', 15))}</span>
      <div>
        <strong style="font-weight: var(--ca-weight-medium);">Check the units</strong>
        <div style="font-size: var(--ca-text-xs); margin-top: var(--ca-space-1);">${r.cautions[0]}</div>
      </div>
    `;
    results.appendChild(caution);
  }

  const uplift = ((r.landedTotal - r.cif) / r.cif) * 100;

  /* ---- Headline ---- */
  const head = document.createElement('div');
  head.style.cssText = 'text-align:center; margin-bottom: var(--ca-space-6);';
  head.innerHTML = html`
    <div class="badge" style="background: color-mix(in srgb, var(--brand-accent) 12%, transparent); color: var(--brand-accent-text);">
      LANDED COST
    </div>
    <div class="stat-figure num" style="margin-top: var(--ca-space-3);">${money(r.landedTotal)}</div>
    <div style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary); margin-top: var(--ca-space-1);">
      <span class="num">${percent(uplift, 1)}</span> above CIF ·
      <span class="num">${money(r.landedTotal / r.containers)}</span> per container
    </div>
  `;
  results.appendChild(head);

  /* ---- The clock, first: it is the only figure that is still moving ---- */
  results.appendChild(demurragePanel(r));

  /* ---- Duty & taxes ---- */
  const taxCard = document.createElement('div');
  taxCard.className = 'card';
  taxCard.style.marginBottom = 'var(--ca-space-4)';
  taxCard.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-3);">Duty &amp; taxes</h3>
    <div class="table-wrap">
      <table>
        <thead>
          <tr><th>Component</th><th>Basis</th><th class="right">Amount</th></tr>
        </thead>
        <tbody>
          ${r.duty.lines.map(
            (line) => html`
              <tr>
                <td style="${line.id === 'cif' ? 'font-weight: var(--ca-weight-medium);' : ''}">${line.label}</td>
                <td style="color: var(--ca-text-quaternary); font-size: var(--ca-text-2xs);">${line.basis}</td>
                <td class="right num">${money(line.amount)}</td>
              </tr>
            `
          )}
          <tr>
            <td style="font-weight: var(--ca-weight-medium);">Total payable to KRA</td>
            <td></td>
            <td class="right num" style="font-weight: var(--ca-weight-medium);">${money(r.duty.totalTax)}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p style="font-size: var(--ca-text-2xs); color: var(--ca-text-quaternary); margin-top: var(--ca-space-3);">
      VAT at ${percent(VAT_RATE * 100)}, IDF at ${percent(IDF_RATE * 100, 1)} of CIF, RDL at
      ${percent(RDL_RATE * 100)} of CIF. Levies are
      ${r.levyOnVatBase ? 'included in' : 'excluded from'} the VAT base.
      Effective tax rate on CIF: <span class="num">${percent((r.duty.totalTax / r.cif) * 100, 1)}</span>.
    </p>
  `;
  results.appendChild(taxCard);

  /* ---- Charges ---- */
  const chargeItems = CHARGE_FIELDS.map((f) => ({ ...f, value: r.charges[f.id] })).filter(
    (c) => c.value > 0
  );

  if (chargeItems.length > 0) {
    const chargeCard = document.createElement('div');
    chargeCard.className = 'card';
    chargeCard.style.marginBottom = 'var(--ca-space-4)';
    chargeCard.innerHTML = html`
      <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-3);">Local charges</h3>
      <div class="stack" style="gap: var(--ca-space-2);">
        ${chargeItems.map(
          (c) => html`
            <div class="row">
              <span style="width:10px;height:10px;border-radius:2px;background:${c.color};flex-shrink:0;"></span>
              <span style="flex:1; font-size: var(--ca-text-sm);">${c.label}</span>
              <span class="num" style="font-size: var(--ca-text-sm);">${money(c.value)}</span>
            </div>
          `
        )}
      </div>
      <div class="row" style="border-top:1px solid var(--ca-border); margin-top: var(--ca-space-3); padding-top: var(--ca-space-3); font-weight: var(--ca-weight-medium);">
        <span style="flex:1;">Total charges</span>
        <span class="num">${money(r.chargesTotal)}</span>
      </div>
    `;
    results.appendChild(chargeCard);
  }

  /* ---- Composition ---- */
  const segments = [
    { label: 'Goods (CIF)', value: r.cif, color: 'var(--ca-chart-1)' },
    { label: 'Duty & taxes', value: r.duty.totalTax, color: 'var(--ca-chart-3)' },
    { label: 'Local charges', value: r.chargesTotal, color: 'var(--ca-chart-5)' },
    { label: 'Demurrage', value: r.demurrage.total, color: 'var(--ca-danger)' }
  ].filter((s) => s.value > 0);

  const composition = document.createElement('div');
  composition.className = 'card';
  composition.style.marginBottom = 'var(--ca-space-4)';
  composition.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-3);">What makes up the cost</h3>
    <div style="display:flex; height:14px; border-radius: var(--ca-radius-pill); overflow:hidden; margin-bottom: var(--ca-space-3);">
      ${segments.map(
        (s) => html`<div
          style="width:${(s.value / r.landedTotal) * 100}%; background:${s.color};"
          title="${s.label}"
        ></div>`
      )}
    </div>
    <div class="stack" style="gap: var(--ca-space-2);">
      ${segments.map(
        (s) => html`
          <div class="row">
            <span style="width:10px;height:10px;border-radius:2px;background:${s.color};flex-shrink:0;"></span>
            <span style="flex:1; font-size: var(--ca-text-sm);">${s.label}</span>
            <span class="num" style="font-size: var(--ca-text-sm);">${money(s.value)}</span>
            <span class="num" style="width:44px;text-align:right;font-size:var(--ca-text-xs);color:var(--ca-text-quaternary);">
              ${((s.value / r.landedTotal) * 100).toFixed(0)}%
            </span>
          </div>
        `
      )}
    </div>
  `;
  results.appendChild(composition);

  results.appendChild(
    PrintButton({
      targetId: 'landed-cost-results',
      title: 'Landed Cost Assessment',
      subtitle: `${r.containers} container${r.containers === 1 ? '' : 's'} · CIF ${money(r.cif)} · ${r.band.label}`
    })
  );

  const again = document.createElement('div');
  again.className = 'no-print';
  again.style.cssText = 'text-align:center; margin-top: var(--ca-space-4);';
  const btn = document.createElement('button');
  btn.className = 'btn btn-ghost';
  btn.type = 'button';
  btn.textContent = 'Change the inputs';
  btn.addEventListener('click', () => {
    results.hidden = true;
    page.querySelector('#lc-form').hidden = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
  again.appendChild(btn);
  results.appendChild(again);

  results.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function demurragePanel(r) {
  const { demurrage, marginal, freeDays, daysHeld, containers } = r;
  const accruing = demurrage.chargeableDays > 0;
  const tone = accruing ? 'danger' : demurrage.daysRemaining <= 1 ? 'warning' : 'info';
  const color =
    tone === 'danger' ? 'var(--ca-danger)' : tone === 'warning' ? 'var(--ca-warning)' : 'var(--ca-info)';

  const card = document.createElement('div');
  card.className = 'card avoid-break';
  card.style.marginBottom = 'var(--ca-space-4)';
  card.style.borderColor = `color-mix(in srgb, ${color} 30%, var(--ca-border))`;

  card.innerHTML = html`
    <div class="row" style="margin-bottom: var(--ca-space-3);">
      <span style="color:${readable(color)};">${raw(icon(accruing ? 'alert' : 'clock', 18))}</span>
      <h3 style="flex:1; font-size: var(--ca-text-sm);">Demurrage &amp; detention</h3>
      <span class="badge" style="background: color-mix(in srgb, ${color} 12%, transparent); color:${readable(color)};">
        ${accruing ? 'Accruing' : demurrage.daysRemaining === 0 ? 'Free time ends today' : 'Within free time'}
      </span>
    </div>

    <div class="grid-2" style="margin-bottom: var(--ca-space-3);">
      <div>
        <div class="stat-figure num" style="font-size: var(--ca-text-xl); color:${readable(color)};">
          ${money(demurrage.total)}
        </div>
        <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          incurred to date${containers > 1 ? html` · ${money(demurrage.perContainer)} per box` : ''}
        </div>
      </div>
      <div>
        <div class="stat-figure num" style="font-size: var(--ca-text-xl);">
          ${accruing ? money(marginal) : `${demurrage.daysRemaining}`}
        </div>
        <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          ${accruing ? 'cost of one more day' : `free ${demurrage.daysRemaining === 1 ? 'day' : 'days'} left`}
        </div>
      </div>
    </div>

    <div style="display:flex; gap:3px; margin-bottom: var(--ca-space-3);">
      ${Array.from({ length: Math.max(freeDays + 6, daysHeld + 2) }, (_, i) => {
        const day = i + 1;
        const isFree = day <= freeDays;
        const isPast = day <= daysHeld;
        const bg = isPast
          ? isFree
            ? 'var(--ca-positive)'
            : 'var(--ca-danger)'
          : isFree
            ? 'color-mix(in srgb, var(--ca-positive) 22%, transparent)'
            : 'var(--ca-surface-sunk)';
        return html`<div
          style="flex:1; height:8px; border-radius:2px; background:${bg};"
          title="Day ${day}${isFree ? ' (free)' : ' (chargeable)'}"
        ></div>`;
      })}
    </div>
    <div class="row" style="font-size: var(--ca-text-2xs); color: var(--ca-text-quaternary); margin-bottom: var(--ca-space-3);">
      <span>Day 1</span>
      <span class="spacer"></span>
      <span><span class="num">${freeDays}</span> free · <span class="num">${daysHeld}</span> elapsed</span>
    </div>

    ${demurrage.breakdown.length > 0
      ? html`
          <div class="table-wrap">
            <table>
              <thead>
                <tr><th>Tier</th><th class="right">Days</th><th class="right">Rate</th><th class="right">Amount</th></tr>
              </thead>
              <tbody>
                ${demurrage.breakdown.map(
                  (b) => html`
                    <tr>
                      <td>${b.label}</td>
                      <td class="right num">${number(b.days)}</td>
                      <td class="right num">${money(b.rate)}</td>
                      <td class="right num">${money(b.amount)}</td>
                    </tr>
                  `
                )}
              </tbody>
            </table>
          </div>
        `
      : html`
          <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
            Nothing has accrued yet. Once free time expires this box starts costing
            ${money(marginal)} a day, escalating by tier.
          </p>
        `}
  `;

  return card;
}

export { DEFAULT_TIERS };
